import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Departures } from 'app/core/enums/common.enum';
import { LocationService } from 'app/modules/sho_management_pro/locations/services/location.service';

import { FinanceResources } from 'app/core/security/resources/resources';
import { PlaydateService } from 'app/modules/sho_management_pro/play-dates/services/playdate.service';
import { VenueFinancesService } from 'app/modules/sho_management_pro/venue/services/venue-finances.service';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { AdapterService } from '../../services/adapters/adapter.service';
import { ConcessionNoveltiesSalesService } from '../../services/sales/concession-novelties/concession-novelties-sales.service';
import { TotalGrossService } from '../../services/sales/total-gross.service';
import * as ConcessionSaleActions from './concession-sales.actions';

@Injectable()
export class ConcessionSalesEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private logger = inject(LoggerService);
    private _saleService = inject(ConcessionNoveltiesSalesService);
    private _alertService = inject(AlertService);
    private adapter = inject(AdapterService);
    private totalGrossService = inject(TotalGrossService);
    private _venueFinancesService = inject(VenueFinancesService);
    private _playdatesService = inject(PlaydateService);
    private _locationService = inject(LocationService);

    messages = {
        getAllError: 'Error getting all Concession Sales',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Concession Sales
    getAllConcessionSales$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ConcessionSaleActions.loadConcessionSales),
            switchMap((action) => {
                return this._saleService
                    .getAll(action.playdateId, Departures.CONCESSION)
                    .pipe(
                        map((res) => {
                            const data = this.adapter.adaptCncNvlSalesList(
                                res.data,
                                FinanceResources.CONCESSION_ITEM
                            );

                            return ConcessionSaleActions.loadConcessionSalesSuccess(
                                {
                                    data: data,
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                ConcessionSaleActions.loadConcessionSalesFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    );
            })
        )
    );

    // Get One Concession Sale
    // getConcessionSale$ = createEffect(() =>
    //     this._actions$.pipe(
    //         ofType(ConcessionSaleActions.loadConcessionSale),
    //         switchMap((action) => {
    //             return this._saleService.getOne(action.id).pipe(
    //                 map((res) => {
    //                     return ConcessionSaleActions.loadConcessionSaleSuccess({
    //                         data: res.data,
    //                     });
    //                 }),
    //                 catchError((error) =>
    //                     of(
    //                         ConcessionSaleActions.loadConcessionSaleFailure({
    //                             error:
    //                                 error?.error?.message ||
    //                                 this.messages.unknownError,
    //                         })
    //                     )
    //                 )
    //             );
    //         })
    //     )
    // );

    // Create Concession Sale
    createConcessionSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ConcessionSaleActions.createConcessionSale),
            switchMap((action) => {
                return this._saleService.create(action.data).pipe(
                    tap((res) => console.log(res)), // Debug
                    map((res) => {
                        const sale = this.adapter.adaptCncNvlSale(
                            res.data,
                            FinanceResources.CONCESSION_ITEM
                        );
                        return ConcessionSaleActions.createConcessionSaleSuccess(
                            {
                                sale: sale,
                                success: 'Concession Sale created successfully',
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            ConcessionSaleActions.createConcessionSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Concession Sale
    updateConcessionSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ConcessionSaleActions.updateConcessionSale),
            switchMap((action) => {
                return this._saleService.update(action.data, action.id).pipe(
                    // tap((res) => console.log(res)), // Debug
                    map((res) => {
                        const sale = this.adapter.adaptCncNvlSale(
                            res.data,
                            FinanceResources.CONCESSION_ITEM
                        );
                        return ConcessionSaleActions.updateConcessionSaleSuccess(
                            {
                                data: sale,
                                success: 'Concession Sale updated successfully',
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            ConcessionSaleActions.updateConcessionSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Sale
    deleteConcessionSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ConcessionSaleActions.deleteConcessionSale),
            switchMap((action) => {
                return this._saleService.delete(action.payload).pipe(
                    // tap((res) => console.log(res)), // Debug
                    map((res) => {
                        const data = res.data;
                        return ConcessionSaleActions.deleteConcessionSaleSuccess(
                            {
                                deletedSales: data.deleted_sales,
                                remainingSales: data.remaining_sales,
                                success: 'Concession Sale deleted successfully',
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            ConcessionSaleActions.deleteConcessionSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Sale Item
    deleteConcessionSaleItem$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ConcessionSaleActions.deleteConcessionSaleItem),
            switchMap((action) => {
                return this._saleService.delete(action.payload).pipe(
                    // tap((res) => console.log(res)), // Debug
                    map((res) => {
                        const saleId = Number(res.data.deleted_sales[0]);
                        const totals = res.data.totals;

                        if (!saleId) {
                            const message =
                                'Sale item ID not found in response';
                            this._alertService.showAlert(message, 'error');
                            throw new Error(message);
                        }

                        return ConcessionSaleActions.deleteConcessionSaleItemSuccess(
                            {
                                deletedSaleItem: saleId,
                                totals,
                                success:
                                    'Concession sale item deleted successfully',
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            ConcessionSaleActions.deleteConcessionSaleItemFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onConcessionSaleActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    ConcessionSaleActions.createConcessionSaleSuccess,
                    ConcessionSaleActions.updateConcessionSaleSuccess,
                    ConcessionSaleActions.deleteConcessionSaleSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');

                    // Get playdate id
                    const playdateId =
                        this._playdatesService.getLatestPlaydateId();

                    this._locationService.checkCommissionsLocations(
                        playdateId,
                        Departures.CONCESSION
                    );

                    // Recalculate commissions
                    // this._commonSalesService
                    //     .addCommission(
                    //         this._playdatesService.getLatestPlaydateId(),
                    //         Departures.CONCESSION
                    //     )
                    //     .subscribe(() => {
                    // Reload totals
                    this._venueFinancesService.onUpdateFinances = true;
                    this.totalGrossService.onValidateCnNvFinances(
                        Departures.CONCESSION
                    );
                    // });
                })
            ),
        { dispatch: false }
    );

    onConcessionSaleActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    ConcessionSaleActions.loadConcessionSalesFailure,
                    ConcessionSaleActions.loadConcessionSaleFailure,
                    ConcessionSaleActions.createConcessionSaleFailure,
                    ConcessionSaleActions.updateConcessionSaleFailure,
                    ConcessionSaleActions.deleteConcessionSaleFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
